import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  People as PeopleIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { getUsers, getUserById, updateUser, deleteUser } from '../../api/adminApi';
import { useToast } from '../../contexts/ToastContext';
import ModernButton from '../../components/common/ModernButton';
import ModernDataGrid from '../../components/common/ModernDataGrid';
import ModernTextField from '../../components/common/ModernTextField';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewDialog, setViewDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const { showToast } = useToast();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (search) params.search = search;
      if (countryFilter) params.country = countryFilter;
      if (statusFilter !== '') params.isActivated = statusFilter;

      const { data } = await getUsers(params);
      setUsers(data.users);
      setTotalPages(data.pagination.pages);
      setTotalCount(data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      showToast('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, countryFilter, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleViewUser = async (user) => {
    try {
      const { data } = await getUserById(user._id);
      setViewDialog(data);
    } catch (error) {
      showToast('Failed to load user details', 'error');
    }
  };

  const handleEditUser = (user) => {
    setEditForm({
      _id: user._id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      country: user.country || '',
      phone: user.phone || '',
      isActivated: user.isActivated,
      isLocked: user.isLocked,
      isAdmin: user.isAdmin || false,
      newPassword: '', // For password change
    });
    setEditDialog(true);
  };

  const handleSaveUser = async () => {
    try {
      const { _id, newPassword, ...updateData } = editForm;
      // Only include password if it was changed
      if (newPassword && newPassword.trim()) {
        updateData.password = newPassword;
      }
      await updateUser(_id, updateData);
      showToast('User updated successfully', 'success');
      setEditDialog(null);
      fetchUsers();
    } catch (error) {
      showToast('Failed to update user', 'error');
    }
  };

  const handleToggleLock = async (user) => {
    try {
      await updateUser(user._id, { isLocked: !user.isLocked });
      showToast(`User ${user.isLocked ? 'unlocked' : 'locked'} successfully`, 'success');
      fetchUsers();
    } catch (error) {
      showToast('Failed to update user status', 'error');
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
            {params.row.firstName[0]}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {params.row.firstName} {params.row.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 100,
    },
    {
      field: 'isActivated',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isActivated ? 'Active' : 'Inactive'}
          color={params.row.isActivated ? 'success' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'isLocked',
      headerName: 'Access',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.isLocked ? 'Locked' : 'Open'}
          color={params.row.isLocked ? 'error' : 'success'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'isAdmin',
      headerName: 'Role',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.isAdmin ? 'Admin' : 'User'}
          color={params.row.isAdmin ? 'secondary' : 'default'}
          size="small"
          sx={{ fontWeight: params.row.isAdmin ? 700 : 400 }}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Joined',
      width: 130,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewUser(params.row)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditUser(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.isLocked ? 'Unlock' : 'Lock'}>
            <IconButton
              size="small"
              onClick={() => handleToggleLock(params.row)}
              color={params.row.isLocked ? 'success' : 'error'}
            >
              {params.row.isLocked ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ backgroundColor: 'primary.main', width: 48, height: 48, mr: 2 }}>
            <PeopleIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Users Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage all registered users
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, p: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <form onSubmit={handleSearch}>
              <TextField
                fullWidth
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </form>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Country"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
            >
              <MenuItem value="">All Countries</MenuItem>
              <MenuItem value="Azerbaijan">Azerbaijan</MenuItem>
              <MenuItem value="Turkey">Turkey</MenuItem>
              <MenuItem value="United Kingdom">United Kingdom</MenuItem>
              <MenuItem value="United States">United States</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={1}>
            <ModernButton fullWidth variant="contained" onClick={fetchUsers}>
              Filter
            </ModernButton>
          </Grid>
        </Grid>
      </Card>

      {/* Data Grid */}
      <ModernDataGrid
        rows={users}
        columns={columns}
        loading={loading}
        getRowId={(row) => row._id}
        pagination
        page={page - 1}
        onPageChange={(newPage) => setPage(newPage + 1)}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(1);
        }}
        rowsPerPageOptions={[20, 50, 100]}
        rowCount={totalCount}
        paginationMode="server"
      />

      {/* View Dialog */}
      {viewDialog && (
        <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="md" fullWidth>
          <DialogTitle>User Details</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">Personal Information</Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      <strong>Name:</strong> {viewDialog.user.firstName} {viewDialog.user.lastName}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Email:</strong> {viewDialog.user.email}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Country:</strong> {viewDialog.user.country || 'N/A'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Phone:</strong> {viewDialog.user.phone || 'N/A'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Role:</strong>{' '}
                      <Chip
                        label={viewDialog.user.isAdmin ? 'Admin' : 'User'}
                        color={viewDialog.user.isAdmin ? 'secondary' : 'default'}
                        size="small"
                      />
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Registrations: {viewDialog.registrations.length}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Transactions: {viewDialog.transactions.length}
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setViewDialog(null)}>Close</ModernButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Dialog */}
      {editDialog && (
        <Dialog open={editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <ModernTextField
                  fullWidth
                  label="First Name"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <ModernTextField
                  fullWidth
                  label="Last Name"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <ModernTextField
                  fullWidth
                  label="Email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <ModernTextField
                  fullWidth
                  label="Country"
                  value={editForm.country}
                  onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <ModernTextField
                  fullWidth
                  label="Phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={editForm.isActivated}
                  onChange={(e) => setEditForm({ ...editForm, isActivated: e.target.value })}
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Access"
                  value={editForm.isLocked}
                  onChange={(e) => setEditForm({ ...editForm, isLocked: e.target.value })}
                >
                  <MenuItem value={false}>Open</MenuItem>
                  <MenuItem value={true}>Locked</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Role"
                  value={editForm.isAdmin}
                  onChange={(e) => setEditForm({ ...editForm, isAdmin: e.target.value })}
                >
                  <MenuItem value={false}>User</MenuItem>
                  <MenuItem value={true}>Admin</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <ModernTextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={editForm.newPassword}
                  onChange={(e) => setEditForm({ ...editForm, newPassword: e.target.value })}
                  placeholder="Leave empty to keep current"
                  helperText="Only fill if changing password"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setEditDialog(null)}>Cancel</ModernButton>
            <ModernButton variant="contained" onClick={handleSaveUser}>
              Save Changes
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default UsersManagement;
